import { QuoteGetResponse } from '@lio/api-types';
import { useEffect, useMemo } from 'react';
import { QuoteStatusChangeItem } from '@models/quoteStatusChange';
import { PortalMessage } from '@components/portalMessage/PortalMessage';
import { PortalMessageType } from '@components/portalMessage/PortalMessageType.enum';
import Loading from '@components/Loading';
import { useSearchQuoteStatusChange } from '@services/quotes';
import { QuoteStatus } from '@models/quotes';
import { Box, ListItem } from '@mui/material';
import { useUWTasksSummary } from '@services/underwritingTasks';
import { DownloadApplicationButton, yellowTextButtonStyles } from '@components/button/DownloadApplicationButton';

export const ReferredQuote = ({ quote }: { quote: QuoteGetResponse }) => {
  const { quoteStatusChange, fetchNextPage } = useSearchQuoteStatusChange({
    quoteId: quote.id,
    pageLimit: 50,
  });
  const allStatusChanges = quoteStatusChange?.pages.flatMap<QuoteStatusChangeItem>((page) => page.items);
  const reasons = useMemo(() => {
    if (allStatusChanges) {
      return allStatusChanges
        ?.filter((el) => el.newQuoteStatus.id === QuoteStatus.Referred.id)
        .reduce((arr, item) => {
          if (item.quoteStatusChangeReason?.reason) {
            arr.push(item.quoteStatusChangeReason.reason);
          }
          return [...new Set(arr)];
        }, [] as string[]);
    }
    return [];
  }, [allStatusChanges]);
  const { underwritingTasksSummary } = useUWTasksSummary(quote?.id);
  const underWritingRules = underwritingTasksSummary?.items.map((item) => item.underwritingRule);
  const filteredUnderWritingRules =
    underWritingRules?.filter((value, index, self) => {
      return self.indexOf(value) === index;
    }) || [];
  const description = filteredUnderWritingRules.length > 0 ? [filteredUnderWritingRules[0].description] : [];

  useEffect(() => {
    const totalItems = quoteStatusChange?.pages[0].pagination.total ?? 0;
    const currentTotalItems = allStatusChanges?.length ?? 0;
    if (totalItems > currentTotalItems) {
      fetchNextPage();
    }
  }, [fetchNextPage, quoteStatusChange, allStatusChanges]);

  if (!quote) return <Loading />;
  const referredReasonsText = [
    <span key='reason-text'>This quote has been referred for the following reason(s):</span>,
  ];

  const subTitleTextArray = [];

  if (description.length > 0)
    subTitleTextArray.push(
      <ListItem
        sx={{
          display: 'list-item',
          fontWeight: 'bold',
        }}
      >
        {description}
      </ListItem>,
    );

  subTitleTextArray.push(
    <Box component='span' sx={{ display: 'flex', flexDirection: 'column' }}>
      Please contact your underwriter with any questions.
      <br />
      <span>
        You may also
        <DownloadApplicationButton
          quoteUpdatedDate={quote?.updatedAt}
          label='download your application'
          quoteId={quote.id}
          buttonProps={{
            color: 'secondary',
            variant: 'text',
            disableRipple: true,
            sx: yellowTextButtonStyles,
          }}
        />
        for your records.
      </span>
    </Box>,
    <span>
      Thank you for your account submission! The premium indication shown is not a formal quote. Terms and conditions
      related to this indication are subject to change based on an underwriter review of the application, loss runs, and
      other submission information. A LIO underwriter has been notified of this submission and will be in touch shortly.
    </span>,
  );

  return (
    <PortalMessage
      type={PortalMessageType.Referred}
      title={['This quote has been referred to underwriting for review.']}
      subtitle={description.length > 0 ? referredReasonsText : []}
      secondarySubtitle={subTitleTextArray}
      primaryBtnText='Message Underwriter'
      primaryBtnAction={`/quote/${quote.id}?viewReadOnly=true&sidebarItem=messages`}
      secondaryBtnText='Continue to your Homepage'
      secondaryBtnAction='/'
      items={reasons}
      quote={quote}
    />
  );
};
